/** @jsxRuntime automatic */
/** @jsxImportSource react */
import { useEffect, useState } from 'react'
import * as THREE from 'three'
import { useFrame } from '@react-three/fiber'
import { frame } from '../flight/state.ts'
import { DESKS } from '../world.ts'
import { loadGLB } from '../assets/loaders.ts'
import { TYPING } from '../shaders/chunks.ts'
import { glsl, SURFACE3 } from '../palette.ts'
import { mulberry32 } from '../prng.ts'

// FOUNDATION §6.6 — every monkey in the field is the SAME animal as the hero:
// a 438-triangle decimation of the hero ape sculpt, instanced once per occupied
// desk (~1500 instances, one draw call). This replaced the 2D atlas billboards,
// whose cells baked in a chair + tiny desk at their own scale — from the god
// views the field read as differently-sized monkeys and empty chairs. Same ink
// shading, same typing strokes (phase-offset per seed), consistent species size.

const VERT = /* glsl */ `
attribute vec3 aPos;
attribute float aYaw;
attribute float aSeed;
attribute float aBirth;
uniform float uTime, uReveal, uFog;
uniform vec3 uBg;
varying vec3 vCol;
${TYPING}
mat3 rotY(float a){ float c=cos(a),s=sin(a); return mat3(c,0.0,s, 0.0,1.0,0.0, -s,0.0,c); }
void main() {
  float s = 0.85 * smoothstep(aBirth, aBirth + 0.03, uReveal);
  vec3 p = typing(position, uTime, aSeed);
  mat3 R = rotY(aYaw);
  vec3 wp = aPos + R * (p * s);
  vec4 mv = viewMatrix * vec4(wp, 1.0);
  gl_Position = projectionMatrix * mv;
  vec3 n = normalize(R * normal);
  vec3 v = normalize(cameraPosition - wp);
  float l = 0.5 + 0.5 * dot(n, normalize(vec3(0.3, 1.0, 0.45)));
  float fill = max(dot(n, v), 0.0) * 0.4;
  vec3 col = mix(vec3(0.106, 0.129, 0.149), vec3(0.306, 0.345, 0.376), clamp(l * 0.85 + fill, 0.0, 1.0));
  float rim = pow(1.0 - abs(dot(n, v)), 2.2);
  col = mix(col, ${glsl(SURFACE3)}, rim * 0.45);
  float dist = length(mv.xyz);
  float fo = 1.0 - exp(-uFog * uFog * dist * dist);
  vCol = mix(col, uBg, fo);
}
`
const FRAG = /* glsl */ `
varying vec3 vCol;
void main() { gl_FragColor = vec4(vCol, 1.0); }
`

export function Monkeys() {
  const [geometry, setGeometry] = useState<THREE.InstancedBufferGeometry | null>(null)
  const [material] = useState(() =>
    new THREE.ShaderMaterial({
      vertexShader: VERT,
      fragmentShader: FRAG,
      uniforms: {
        uTime: { value: 0 },
        uReveal: { value: 0 },
        uFog: { value: 0.016 },
        uBg: { value: new THREE.Color() },
      },
    })
  )

  useEffect(() =>
    loadGLB('/assets/field-ape.glb', (base) => {
      base.computeVertexNormals()
      base.computeBoundingBox()
      base.translate(0, -base.boundingBox!.min.y, 0) // ground the feet

      // hero desk (index 0) has the dedicated high-res ape (HeroApe.tsx)
      const occupied = DESKS.filter((d, i) => i > 0 && d.monkey)
      const N = occupied.length
      const geo = new THREE.InstancedBufferGeometry()
      geo.index = base.index
      geo.setAttribute('position', base.attributes.position)
      geo.setAttribute('normal', base.attributes.normal)
      const pos = new Float32Array(N * 3)
      const yaw = new Float32Array(N)
      const seed = new Float32Array(N)
      const birth = new Float32Array(N)
      const rand = mulberry32(999)
      occupied.forEach((d, i) => {
        // seated on the camera side of the desk, facing it
        pos[i * 3] = d.x - Math.sin(d.yaw) * 0.78
        pos[i * 3 + 1] = 0
        pos[i * 3 + 2] = d.z - Math.cos(d.yaw) * 0.78
        yaw[i] = d.yaw
        seed[i] = rand() * 120
        birth[i] = d.birth
      })
      geo.setAttribute('aPos', new THREE.InstancedBufferAttribute(pos, 3))
      geo.setAttribute('aYaw', new THREE.InstancedBufferAttribute(yaw, 1))
      geo.setAttribute('aSeed', new THREE.InstancedBufferAttribute(seed, 1))
      geo.setAttribute('aBirth', new THREE.InstancedBufferAttribute(birth, 1))
      geo.instanceCount = N
      setGeometry(geo)
    }), [])

  useFrame((st) => {
    const b = frame.beats
    const u = material.uniforms
    u.uTime.value = st.clock.elapsedTime
    u.uReveal.value = b.reveal
    u.uFog.value = b.fog
    ;(u.uBg.value as THREE.Color).copy(frame.bg)
  })

  if (!geometry) return null
  return <mesh geometry={geometry} material={material} frustumCulled={false} />
}
